import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Home,
  Menu,
  Moon,
  RefreshCw,
  Search,
  Sun,
  X
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { usePermissions } from 'hooks/usePermissions';
import { usePagePermissions } from 'hooks/usePagePermissions';
import { useSolicitacoesNotifications } from 'hooks/useSolicitacoesNotifications';
import { getCompanyContext, setCompanyContext, setGroupContext } from 'utils/setCompanyContext';
import HeaderDirectorySwitcher from './HeaderDirectorySwitcher';
import MobileUserMenu from './MobileUserMenu';
import PageHeaderTitle from './PageHeaderTitle';

const DEFAULT_COMPANY_COLOR = '#3b82f6';

const clampChannel = (value) => Math.min(255, Math.max(0, value));

const normalizeHex = (hex) => {
  if (!hex) return DEFAULT_COMPANY_COLOR;
  const sanitized = hex.replace('#', '');
  if (sanitized.length === 3) {
    return `#${sanitized
      .split('')
      .map((c) => `${c}${c}`)
      .join('')}`;
  }
  if (sanitized.length !== 6) {
    return DEFAULT_COMPANY_COLOR;
  }
  return `#${sanitized}`;
};

const adjustTone = (hex, amount = 0) => {
  const normalized = normalizeHex(hex);
  const num = parseInt(normalized.slice(1), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const mix = (channel) => {
    if (amount < 0) {
      return clampChannel(channel * (1 + amount));
    }
    return clampChannel(channel + (255 - channel) * amount);
  };
  const nr = mix(r);
  const ng = mix(g);
  const nb = mix(b);
  const hexString = (value) => value.toString(16).padStart(2, '0');
  return `#${hexString(nr)}${hexString(ng)}${hexString(nb)}`;
};

const getCompanyPrimaryColor = (company, availableCompanies) => {
  if (company?.primary_color) return company.primary_color;
  const match = availableCompanies?.find((candidate) => candidate.id === company?.id);
  if (match?.primary_color) return match.primary_color;
  if (company?.color) return company.color;
  return DEFAULT_COMPANY_COLOR;
};

const getCompanyColor = (company, availableCompanies) => {
  if (company?.isGroup) {
    return company?.color || '#6366f1';
  }
  return getCompanyPrimaryColor(company, availableCompanies);
};

const buildCompanyPalette = (company, companies) => {
  const primary = getCompanyPrimaryColor(company, companies);
  return {
    primary,
    dark: adjustTone(primary, -0.35),
    darker: adjustTone(primary, -0.55),
    light: adjustTone(primary, 0.2),
    gradient: `linear-gradient(120deg, ${adjustTone(primary, -0.15)} 0%, ${adjustTone(primary, 0.18)} 100%)`
  };
};

const getReadableTextColor = (hex) => {
  try {
    const normalized = normalizeHex(hex);
    const value = parseInt(normalized.slice(1), 16);
    if (Number.isNaN(value)) return '#f8fafc';

    const r = (value >> 16) & 0xff;
    const g = (value >> 8) & 0xff;
    const b = value & 0xff;
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    return luminance > 0.62 ? '#0f172a' : '#f8fafc';
  } catch (error) {
    return '#f8fafc';
  }
};

const getCompanyInitials = (company) => {
  if (company?.initials) return company.initials.toUpperCase();
  if (company?.isGroup) return 'HG';
  if (company?.name) return company.name.slice(0, 2).toUpperCase();
  return 'MV';
};

const buildConsolidatedLabel = (name, count) => {
  const baseName = name || 'Horizon Group';
  return `${baseName} (Consolidado${count ? ` • ${count} empresa${count === 1 ? '' : 's'}` : ''})`;
};

const useDismissableLayer = (isOpen, onClose, refs) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      const clickedInside = refs.some((ref) => ref.current && ref.current.contains(target));
      if (!clickedInside) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, refs]);
};

const HeaderShell = ({ children }) => (
  <header className="sticky top-0 z-50 border-b border-[color:var(--header-divider)] bg-[color:var(--surface)]/92 backdrop-blur-xl">
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="relative flex h-[var(--header-shell-height)] items-center gap-[var(--header-gap)] lg:grid lg:grid-cols-[minmax(0,620px)_minmax(200px,1fr)_auto] lg:gap-6">
        {children}
      </div>
    </div>
  </header>
);

const CompanySwitcher = ({
  open,
  onToggle,
  onClose,
  selectedCompany,
  filteredCompanies,
  companySearch,
  onSearchChange,
  onSelectCompany,
  isChangingCompany,
  statusMessage,
  availableCompanies
}) => {
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const [customConsolidationOpen, setCustomConsolidationOpen] = useState(false);
  const [selectedConsolidatedIds, setSelectedConsolidatedIds] = useState([]);
  useDismissableLayer(open, onClose, [rootRef]);

  const shortStatus = isChangingCompany
    ? 'Sincronizando'
    : statusMessage?.replace('Atualizado às ', '') || 'Contexto ativo';
  const currentCompanySwatch = getCompanyColor(selectedCompany, availableCompanies);
  const currentCompanyTextColor = getReadableTextColor(currentCompanySwatch);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    try {
      const companyContext = getCompanyContext();
      if (companyContext?.isConsolidated || companyContext?.isGroup) {
        setSelectedConsolidatedIds(Array.isArray(companyContext?.ids) ? companyContext.ids : []);
      } else {
        setSelectedConsolidatedIds([]);
      }
    } catch (error) {
      setSelectedConsolidatedIds([]);
    }
  }, [open]);

  const toggleConsolidatedCompany = (companyId) => {
    setSelectedConsolidatedIds((current) => (
      current.includes(companyId)
        ? current.filter((id) => id !== companyId)
        : [...current, companyId]
    ));
  };

  const applyCustomConsolidation = () => {
    const selectedCompanies = (availableCompanies || []).filter((company) => selectedConsolidatedIds.includes(company.id));
    if (selectedCompanies.length === 0) return;

    const groupName = selectedCompany?.group?.name || selectedCompany?.name || 'Horizon Group';
    const label = buildConsolidatedLabel(groupName, selectedCompanies.length);
    const consolidatedCompany = {
      id: 'group',
      name: label,
      slug: 'horizon-group',
      isGroup: true,
      isConsolidated: true,
      selectedCompanies,
      availableCompanies: availableCompanies || [],
      companies: selectedCompanies,
      group: {
        id: 'group',
        name: label,
        slug: 'horizon-group'
      }
    };

    onSelectCompany(consolidatedCompany);
    setCustomConsolidationOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1 lg:max-w-[352px]">
      <button
        type="button"
        onClick={onToggle}
        disabled={isChangingCompany}
        className={`group flex h-[44px] w-full items-center justify-between gap-2.5 rounded-[14px] border bg-[color:var(--surface)] px-2 text-left text-[color:var(--text-primary)] transition-all duration-200 hover:bg-[color:var(--header-hover-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] active:scale-[0.995] ${
          open ? 'border-[color:var(--accent)]/35' : 'border-[color:var(--header-control-border-subtle)]'
        } ${
          isChangingCompany ? 'cursor-progress opacity-70' : ''
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Selecionar empresa"
        title={shortStatus}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[12px] border border-[color:var(--header-border)] text-[11px] font-bold shadow-[0_6px_14px_rgba(0,0,0,0.12)]"
            style={{ background: currentCompanySwatch, color: currentCompanyTextColor }}
          >
            {isChangingCompany ? <RefreshCw className="h-4 w-4 animate-spin" /> : getCompanyInitials(selectedCompany)}
          </div>
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-[14px] font-semibold text-[color:var(--text-primary)]">
              {selectedCompany?.name || 'MOV Fibra'}
            </p>
            <span className="inline-flex rounded-full border border-[color:var(--header-border)] bg-[color:var(--surface-muted)] px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.1em] text-[color:var(--text-muted)]">
              {selectedCompany?.isGroup ? 'Grupo' : 'Ativa'}
            </span>
          </div>
        </div>

        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)] transition-transform group-hover:text-[color:var(--text-primary)] ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-[70] mt-2 origin-top animate-[header-pop_180ms_ease-out] rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] p-2 text-[color:var(--text-primary)] shadow-[0_20px_50px_rgba(0,0,0,0.18)] lg:right-auto lg:w-[200%] lg:max-w-[760px]">
        <div className="sticky top-0 z-10 rounded-2xl bg-[color:var(--surface)] px-2 pb-2 pt-1">
          <div className="flex items-center justify-between gap-3 px-1 pb-2">
            <p className="text-sm font-semibold text-[color:var(--text-primary)]">Selecionar empresa</p>
            <p className="text-[11px] text-[color:var(--text-muted)]/80">{shortStatus}</p>
          </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                ref={searchInputRef}
                className="min-h-[44px] w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] py-2 pl-10 pr-3 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                placeholder="Buscar empresa..."
                value={companySearch}
                onChange={(event) => onSearchChange(event.target.value)}
              />
            </div>
            {availableCompanies?.length > 1 && (
              <div className="mt-3 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--accent)]">
                      Consolidado personalizado
                    </p>
                    <p className="mt-1 text-[11px] text-[color:var(--text-muted)]">
                      {selectedConsolidatedIds.length > 0
                        ? `${selectedConsolidatedIds.length} empresa(s) selecionada(s)`
                        : 'Escolha quais empresas entram no consolidado'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCustomConsolidationOpen((current) => !current)}
                    className="shrink-0 rounded-md bg-[color:var(--accent)] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                  >
                    {customConsolidationOpen ? 'Fechar' : 'Editar'}
                  </button>
                </div>

                {customConsolidationOpen && (
                  <div className="mt-3 space-y-2">
                    {(availableCompanies || []).map((company) => {
                      const isSelected = selectedConsolidatedIds.includes(company.id);
                      return (
                        <button
                          key={company.id}
                          type="button"
                          onClick={() => toggleConsolidatedCompany(company.id)}
                          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors ${
                            isSelected
                              ? 'border-[color:var(--accent)] bg-[color:var(--surface)]'
                              : 'border-[color:var(--border)] bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)]'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-[color:var(--text-primary)]">
                              {String(company.name || 'Empresa')}
                            </div>
                            {company.company_type && (
                              <div className="truncate text-xs text-[color:var(--text-muted)]">
                                {company.company_type}
                              </div>
                            )}
                          </div>
                          {isSelected && <Check className="h-4 w-4 flex-shrink-0 text-[color:var(--accent)]" />}
                        </button>
                      );
                    })}

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setSelectedConsolidatedIds((availableCompanies || []).map((company) => company.id))}
                        className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-xs font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]"
                      >
                        Todas
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedConsolidatedIds([])}
                        className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-xs font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]"
                      >
                        Limpar
                      </button>
                      <button
                        type="button"
                        onClick={applyCustomConsolidation}
                        disabled={selectedConsolidatedIds.length === 0}
                        className="ml-auto rounded-md bg-[color:var(--accent)] px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Aplicar consolidado
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="relative">
            <ul className="max-h-72 overflow-auto pr-1" role="listbox">
            {filteredCompanies.map((company) => (
              <li key={company.id || 'default'}>
                {company.isGroupHeader ? (
                  <div className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--text-muted)]/60">
                    {company.name}
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`relative flex min-h-[50px] w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition ${
                      company.id === selectedCompany?.id
                        ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] shadow-[inset_0_0_0_1px_var(--header-border)]'
                        : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                    } ${company.level === 1 ? 'pl-7' : ''}`}
                    onClick={() => onSelectCompany(company)}
                  >
                    <span
                      className={`absolute bottom-2 left-0 top-2 w-1 rounded-r-full ${
                        company.id === selectedCompany?.id ? 'bg-[color:var(--accent)]' : 'bg-transparent'
                      }`}
                      aria-hidden="true"
                    />
                    {company.level === 1 && <span className="h-px w-4 bg-[color:var(--border)]" aria-hidden="true" />}
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[color:var(--header-border)] text-[11px] font-semibold text-[color:var(--avatar-text)]"
                      style={{
                        background: getCompanyColor(company, availableCompanies),
                        color: getReadableTextColor(getCompanyColor(company, availableCompanies))
                      }}
                    >
                      {getCompanyInitials(company)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{company.name}</span>
                      {company.group?.name && !company.isGroup && (
                        <span className="block truncate text-xs text-[color:var(--text-muted)]/85">{company.group.name}</span>
                      )}
                    </span>
                    <span
                      className={`ml-1 h-2.5 w-2.5 rounded-full ${
                        company.id === selectedCompany?.id ? 'opacity-100' : 'opacity-45'
                      }`}
                      style={{ background: getCompanyPrimaryColor(company, availableCompanies) }}
                      aria-hidden="true"
                    />
                    {company.id === selectedCompany?.id && (
                      <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-[color:var(--header-border)] bg-[color:var(--surface)]">
                        <Check className="h-3.5 w-3.5 text-[color:var(--accent)]" />
                      </span>
                    )}
                  </button>
                )}
              </li>
            ))}
            {filteredCompanies.length === 0 && (
              <li className="px-3 py-4 text-center text-xs text-[color:var(--text-muted)]">Nenhuma empresa encontrada</li>
            )}
            </ul>
            <div className="pointer-events-none absolute inset-x-1 bottom-0 h-5 rounded-b-2xl bg-gradient-to-t from-[color:var(--surface)] to-transparent" />
          </div>
        </div>
      )}
    </div>
  );
};

const HeaderActions = ({ homePath, navigate, onRefresh, loading, isChangingCompany, showRefreshButton }) => (
  <div className="flex h-[var(--header-control-height)] items-center gap-2.5">
    <IconButton title="Página inicial" onClick={() => navigate(homePath)} ghost>
      <Home className="h-4 w-4" />
    </IconButton>
    {showRefreshButton && onRefresh && (
      <IconButton
        title={loading || isChangingCompany ? 'Atualizando dados...' : 'Atualizar dados'}
        onClick={onRefresh}
        disabled={loading || isChangingCompany}
        ghost
      >
        <RefreshCw className={`h-4 w-4 ${(loading || isChangingCompany) ? 'animate-spin' : ''}`} />
      </IconButton>
    )}
  </div>
);

const MobileNavSheet = ({
  open,
  onClose,
  user,
  selectedCompany,
  companyPalette,
  statusMessage,
  homePath,
  directoryItems,
  currentDirectory,
  isGlobalDirectoryRoute,
  onNavigate,
  onSelectDirectory,
  filteredCompanies,
  companySearch,
  onSearchChange,
  onSelectCompany,
  availableCompanies,
  showRefreshButton,
  onRefresh,
  loading,
  isChangingCompany,
  canAccessSettings,
  canAccessRequests,
  pendingCount,
  currentTheme,
  onUpdateTheme,
  onLogout
}) => {
  const panelRef = useRef(null);
  useDismissableLayer(open, onClose, [panelRef]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] lg:hidden" role="dialog" aria-modal="true" aria-label="Menu de navegação">
      <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm" />
      <div
        ref={panelRef}
        className="absolute inset-y-0 left-0 flex h-full w-full max-w-sm flex-col bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-[color:var(--border)] px-4 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">Navegação</p>
            <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">Dashboard Horizon</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[color:var(--border)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <section className="rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4">
            <div className="flex items-center gap-3">
              <div
                className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-semibold text-[color:var(--avatar-text)]"
                style={{ background: companyPalette.primary, color: getReadableTextColor(companyPalette.primary) }}
              >
                {isChangingCompany ? <RefreshCw className="h-4 w-4 animate-spin" /> : getCompanyInitials(selectedCompany)}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-muted)]">
                  {selectedCompany?.isGroup ? 'Grupo ativo' : 'Empresa ativa'}
                </p>
                <p className="truncate text-base font-semibold text-[color:var(--text-primary)]">
                  {selectedCompany?.name || 'MOV Fibra'}
                </p>
                <p className="truncate text-xs text-[color:var(--text-muted)]">{statusMessage}</p>
              </div>
            </div>

            <div className={`mt-4 grid gap-2 ${showRefreshButton && onRefresh ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <button
                  type="button"
                  onClick={() => onNavigate(homePath)}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] transition hover:bg-[color:var(--surface)]/80"
                >
                  <Home className="h-4 w-4" />
                  Início
                </button>
                {showRefreshButton && onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={loading || isChangingCompany}
                  className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border border-[color:var(--border)] px-4 py-2 text-sm font-medium transition ${
                    loading || isChangingCompany
                      ? 'cursor-not-allowed bg-[color:var(--surface)] text-[color:var(--text-muted)]'
                      : 'bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface)]/80'
                  }`}
                >
                  <RefreshCw className={`h-4 w-4 ${(loading || isChangingCompany) ? 'animate-spin' : ''}`} />
                  {loading || isChangingCompany ? 'Atualizando...' : 'Atualizar'}
                </button>
                )}
              </div>
          </section>

          {directoryItems.length > 0 && (
            <section className="rounded-3xl border border-[color:var(--border)] p-3">
              <p className="px-2 text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-muted)]">Diretoria</p>
              <div className="mt-3">
                <HeaderDirectorySwitcher
                  directories={directoryItems}
                  currentDirectory={currentDirectory}
                  isGlobal={isGlobalDirectoryRoute}
                  onSelectDirectory={(directory) => onSelectDirectory?.(directory)}
                  mobile
                />
              </div>
            </section>
          )}

          <section className="rounded-3xl border border-[color:var(--border)] p-3">
            <p className="px-2 text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-muted)]">Trocar empresa</p>
            <input
              className="mt-3 min-h-[44px] w-full rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
              placeholder="Buscar empresa..."
              value={companySearch}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            <div className="mt-3 max-h-60 space-y-1 overflow-y-auto pr-1">
              {filteredCompanies.map((company) => (
                <button
                  key={company.id || 'default'}
                  type="button"
                  className={`flex min-h-[48px] w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm transition ${
                    company.id === selectedCompany?.id
                      ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
                      : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                  } ${company.level === 1 ? 'pl-7' : ''} ${company.isGroupHeader ? 'cursor-default opacity-70' : ''}`}
                  onClick={() => {
                    if (company.isGroupHeader) return;
                    onSelectCompany(company);
                  }}
                  disabled={company.isGroupHeader || isChangingCompany}
                >
                  {company.level === 1 && <span className="h-px w-4 bg-[color:var(--border)]" aria-hidden="true" />}
                  <span
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-semibold text-[color:var(--avatar-text)]"
                    style={{
                      background: getCompanyColor(company, availableCompanies),
                      color: getReadableTextColor(getCompanyColor(company, availableCompanies))
                    }}
                  >
                    {getCompanyInitials(company)}
                  </span>
                  <span className="truncate">{company.name}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="border-t border-[color:var(--border)] px-4 py-4">
          <MobileUserMenu
            user={user}
            onLogout={onLogout}
            onThemeChange={onUpdateTheme}
            onNavigate={onNavigate}
            canAccessSettings={canAccessSettings}
            canAccessRequests={canAccessRequests}
            pendingCount={pendingCount}
            currentTheme={currentTheme}
            accentColor={companyPalette.primary}
          />
        </div>
      </div>
    </div>
  );
};

const LogoutModal = ({ open, onCancel, onConfirm, loading }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6 text-[color:var(--text-primary)] shadow-[0_24px_60px_rgba(0,0,0,0.24)]">
        <h2 className="text-lg font-bold text-[color:var(--text-primary)]">Confirmar saída</h2>
        <p className="mt-2 text-sm text-[color:var(--text-muted)]">Tem certeza de que deseja sair do sistema?</p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Sair'}
          </button>
        </div>
      </div>
    </div>
  );
};

function IconButton({ children, title, onClick, disabled = false, ghost = false }) {
  return (
    <button
      type="button"
      className={`inline-flex h-[var(--header-control-height)] w-[var(--header-control-height)] items-center justify-center rounded-xl text-[color:var(--text-muted)] transition-all duration-200 hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] active:scale-[0.98] ${
        ghost
          ? 'border border-[color:var(--header-control-border-subtle)] bg-transparent hover:bg-[color:var(--header-hover-soft)]'
          : 'border border-[color:var(--border)] bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)]'
      } ${
        disabled ? 'cursor-not-allowed opacity-40' : ''
      }`}
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="leading-none [&_svg]:h-[18px] [&_svg]:w-[18px] [&_svg]:stroke-[1.85]">{children}</span>
    </button>
  );
}

function UserMenu({
  onProfile,
  onLogout,
  user,
  canAccessSettings,
  canAccessRequests,
  pendingCount,
  onNavigateToSettings,
  onNavigateToRequests,
  avatarColor,
  userEmail,
  accentColor,
  currentTheme = 'light',
  onUpdateTheme
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useDismissableLayer(open, () => setOpen(false), [rootRef]);

  const displayName = user?.full_name || user?.name || 'Usuário';
  const displayEmail = userEmail || user?.corporate_email || user?.email || 'Conta corporativa';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex h-[var(--header-control-height)] max-w-[180px] items-center gap-2 rounded-xl border bg-[color:var(--surface)] px-1.5 text-left text-[color:var(--text-primary)] transition-all duration-200 hover:bg-[color:var(--header-hover-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] active:scale-[0.995] ${
          open ? 'border-[color:var(--accent)]/35' : 'border-[color:var(--header-control-border-subtle)]'
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Abrir menu do usuário"
      >
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-[color:var(--avatar-text)]"
          style={{ background: avatarColor || '#0f172a' }}
        >
          {user?.full_name?.charAt(0) || user?.name?.charAt(0) || 'U'}
        </span>
        <div className="hidden min-w-0 max-w-[112px] flex-1 text-left xl:block">
          <span className="block truncate text-sm font-semibold leading-tight text-[color:var(--text-primary)]">{displayName}</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-[color:var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-[70] mt-2 w-72 origin-top animate-[header-pop_180ms_ease-out] rounded-3xl border border-[color:var(--border)] bg-[color:var(--surface)] p-2 text-[color:var(--text-primary)] shadow-[0_20px_50px_rgba(0,0,0,0.16)]">
          <div className="px-3 pb-2 pt-1">
            <p className="truncate text-sm font-semibold text-[color:var(--text-primary)]">{displayName}</p>
            <p className="truncate text-xs text-[color:var(--text-muted)]">{displayEmail}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              onProfile?.();
              setOpen(false);
            }}
            className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
          >
            Meu perfil
          </button>
          {canAccessSettings && (
            <button
              type="button"
              onClick={() => {
                onNavigateToSettings?.();
                setOpen(false);
              }}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
            >
              Configurações
            </button>
          )}
          {canAccessRequests && (
            <button
              type="button"
              onClick={() => {
                onNavigateToRequests?.();
                setOpen(false);
              }}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
            >
              Gestão de solicitações
              {pendingCount > 0 && (
                <span className="ml-auto rounded-full bg-red-500/85 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {pendingCount > 99 ? '99+' : pendingCount}
                </span>
              )}
            </button>
          )}
          <div className="my-2 h-px bg-[color:var(--surface-muted)]" />
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">Tema</p>
            <div className="mt-2 flex rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-1">
              <button
                type="button"
                onClick={() => onUpdateTheme?.('light')}
                className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold transition ${
                  currentTheme === 'light'
                    ? 'text-white shadow-sm'
                    : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                }`}
                style={currentTheme === 'light' ? { background: accentColor || 'var(--accent)' } : undefined}
              >
                <Sun className="mr-1 inline h-3.5 w-3.5" />
                Claro
              </button>
              <button
                type="button"
                onClick={() => onUpdateTheme?.('dark')}
                className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold transition ${
                  currentTheme === 'dark'
                    ? 'text-white shadow-sm'
                    : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
                }`}
                style={currentTheme === 'dark' ? { background: accentColor || 'var(--accent)' } : undefined}
              >
                <Moon className="mr-1 inline h-3.5 w-3.5" />
                Escuro
              </button>
            </div>
          </div>
          <div className="my-2 h-px bg-[color:var(--surface-muted)]" />
          <button
            type="button"
            onClick={() => {
              onLogout?.();
              setOpen(false);
            }}
            className="w-full rounded-xl px-3 py-2 text-left text-sm font-semibold text-rose-300 hover:bg-rose-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
          >
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

const GlobalHeader = ({ ultimaAtualizacao, loading, atualizarDados, showRefreshButton = true }) => {
  const { user, logout, updateThemePreference } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { getNavigationItems, getCurrentDirectory, isGlobalRoute } = usePagePermissions();
  const { pendingCount } = useSolicitacoesNotifications();

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [openCompanies, setOpenCompanies] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [isChangingCompany, setIsChangingCompany] = useState(false);

  useEffect(() => {
    const updateSelectedCompany = () => {
      const companyContext = getCompanyContext();
      if (companyContext?.companies && companyContext.companies.length > 0) {
        if (companyContext.isGroup || companyContext.isConsolidated) {
          const groupName = companyContext?.group?.name || 'Horizon Group';
          const selectedCount = companyContext.companies.length;
          setSelectedCompany({
            id: 'group',
            name: groupName.toLowerCase().includes('consolidado')
              ? groupName
              : `${groupName} (Consolidado${selectedCount ? ` • ${selectedCount} empresa${selectedCount === 1 ? '' : 's'}` : ''})`,
            slug: 'horizon-group',
            isGroup: true,
            color: '#6366f1',
            initials: 'HG'
          });
        } else {
          setSelectedCompany(companyContext.companies[0]);
        }
      } else if (user?.accessible_companies && user.accessible_companies.length > 0) {
        setSelectedCompany(user.accessible_companies[0]);
      }
    };

    updateSelectedCompany();
    const handleContextChange = () => updateSelectedCompany();
    window.addEventListener('company-context-changed', handleContextChange);

    return () => window.removeEventListener('company-context-changed', handleContextChange);
  }, [user?.accessible_companies]);

  const navigationItems = getNavigationItems();
  const currentDirectory = getCurrentDirectory(location.pathname);
  const isGlobalDirectoryRoute = isGlobalRoute(location.pathname);
  const canAccessSettings = hasPermission('config.usuarios.view');
  const canAccessRequests = hasPermission('solicitacoes.view');

  const getHomePage = () => {
    if (hasPermission('dashboard.view')) {
      return '/dashboard';
    }
    const accessibleItems = navigationItems.filter((item) => item.visible);
    if (accessibleItems.length > 0) {
      return accessibleItems[0].path;
    }
    return '/dashboard';
  };

  const homePath = getHomePage();

  const filteredCompanies = useMemo(() => {
    if (!user?.accessible_companies) return [];

    const companiesByGroup = {};
    const companiesWithoutGroup = [];

    user.accessible_companies.forEach((company) => {
      if (company.group) {
        const groupId = company.group.id;
        if (!companiesByGroup[groupId]) {
          companiesByGroup[groupId] = {
            group: company.group,
            companies: []
          };
        }
        companiesByGroup[groupId].companies.push(company);
      } else {
        companiesWithoutGroup.push(company);
      }
    });

    const hierarchicalList = [];

    if (user.accessible_companies.length > 1) {
      hierarchicalList.push({
        id: 'group',
        name: 'Horizon Group (Consolidado)',
        slug: 'horizon-group',
        isGroup: true,
        color: '#6366f1',
        initials: 'HG',
        level: 0
      });
    }

    Object.values(companiesByGroup).forEach((groupData) => {
      hierarchicalList.push({
        id: `group-${groupData.group.id}`,
        name: groupData.group.name,
        slug: groupData.group.slug,
        isGroupHeader: true,
        color: '#8b5cf6',
        initials: groupData.group.name.slice(0, 2).toUpperCase(),
        level: 0
      });

      groupData.companies.forEach((company) => {
        hierarchicalList.push({
          ...company,
          parentGroupId: groupData.group.id,
          level: 1,
          isCompany: true
        });
      });
    });

    companiesWithoutGroup.forEach((company) => {
      hierarchicalList.push({
        ...company,
        parentGroupId: null,
        level: 0,
        isCompany: true
      });
    });

    const query = companySearch.trim().toLowerCase();
    if (!query) return hierarchicalList;

    const filtered = [];
    let pendingGroupHeader = null;

    hierarchicalList.forEach((item) => {
      const matches = item.name.toLowerCase().includes(query);

      if (item.isGroupHeader) {
        pendingGroupHeader = item;
        return;
      }

      if (item.isGroup && matches) {
        filtered.push(item);
        return;
      }

      if (matches) {
        if (item.parentGroupId && pendingGroupHeader && !filtered.some((entry) => entry.id === pendingGroupHeader.id)) {
          filtered.push(pendingGroupHeader);
        }
        filtered.push(item);
      }
    });

    return filtered;
  }, [user?.accessible_companies, companySearch]);

  const companyPalette = useMemo(
    () => buildCompanyPalette(selectedCompany, user?.accessible_companies),
    [selectedCompany, user?.accessible_companies]
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (companyPalette?.primary) {
      document.documentElement.style.setProperty('--accent', companyPalette.primary);
    }
  }, [companyPalette?.primary]);

  useEffect(() => {
    setOpenCompanies(false);
    setOpenDrawer(false);
    setCompanySearch('');
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.style.overflow = openDrawer ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [openDrawer]);

  const formattedUpdateTime = ultimaAtualizacao?.toLocaleTimeString?.('pt-BR');
  const currentTheme = useMemo(() => {
    if (user?.theme) return user.theme;
    try {
      const stored = localStorage.getItem('theme_preference');
      return stored === 'dark' ? 'dark' : 'light';
    } catch (error) {
      return 'light';
    }
  }, [user?.theme]);

  const statusMessage = isChangingCompany
    ? 'Sincronizando dados da empresa...'
    : formattedUpdateTime
      ? `Atualizado às ${formattedUpdateTime}`
      : currentDirectory
        ? `Diretoria atual: ${currentDirectory.label}`
        : 'Contexto global';

  const handleCompanyChange = async (company, options = {}) => {
    setIsChangingCompany(true);
    setSelectedCompany(company);

    try {
      if (company.isGroup || company.isConsolidated || company.selectedCompanies) {
        const selectedCompanies = company.selectedCompanies || company.companies || [];
        const availableCompanies = company.availableCompanies || user.accessible_companies || selectedCompanies;
        setGroupContext(availableCompanies, selectedCompanies, company.group || company);
      } else {
        setCompanyContext(company.id, company.name, company.slug, company.type);
      }

      window.dispatchEvent(new CustomEvent('company-changed', { detail: { company } }));

      if (atualizarDados) {
        await atualizarDados();
      }
    } catch (error) {
      console.error('Erro ao atualizar dados após mudança de empresa:', error);
    } finally {
      setIsChangingCompany(false);
      setOpenCompanies(false);
      setCompanySearch('');
      if (options.closeDrawer) {
        setOpenDrawer(false);
      }
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setIsLoggingOut(false);
      setIsLogoutModalOpen(false);
    }
  };

  const handleRefresh = async () => {
    if (!atualizarDados) return;
    try {
      await atualizarDados();
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    }
  };

  const handleNavigateAndCloseDrawer = (path) => {
    navigate(path);
    setOpenDrawer(false);
  };

  return (
    <>
      <a
        href="#main-content"
        className="sr-only absolute left-4 top-4 z-[100] rounded-xl bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-[color:var(--text-primary)] focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
      >
        Pular para o conteúdo
      </a>

      <HeaderShell>
        <div className="relative flex min-w-0 items-center gap-3 lg:gap-4 lg:z-10">
          <button
            type="button"
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border border-[color:var(--border)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] lg:hidden"
            onClick={() => setOpenDrawer(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="min-w-0 flex-1 lg:hidden">
            <div className="flex items-center gap-3 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-3">
              <div
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl text-sm font-semibold text-[color:var(--avatar-text)]"
                style={{ background: companyPalette.primary, color: getReadableTextColor(companyPalette.primary) }}
              >
                {getCompanyInitials(selectedCompany)}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[color:var(--text-primary)]">
                  {selectedCompany?.name || 'MOV Fibra'}
                </p>
                <p className="truncate text-xs text-[color:var(--text-muted)]">{statusMessage}</p>
              </div>
            </div>
          </div>

          <div className="hidden min-w-0 shrink-0 lg:block">
            <CompanySwitcher
              open={openCompanies}
              onToggle={() => {
                setOpenCompanies((value) => !value);
              }}
              onClose={() => setOpenCompanies(false)}
              selectedCompany={selectedCompany}
              filteredCompanies={filteredCompanies}
              companySearch={companySearch}
              onSearchChange={setCompanySearch}
              onSelectCompany={(company) => handleCompanyChange(company)}
              isChangingCompany={isChangingCompany}
              statusMessage={statusMessage}
              availableCompanies={user?.accessible_companies}
            />
          </div>

          <div className="hidden min-w-0 shrink-0 lg:block">
            <HeaderDirectorySwitcher
              directories={navigationItems}
              currentDirectory={currentDirectory}
              isGlobal={isGlobalDirectoryRoute}
              onSelectDirectory={(directory) => navigate(directory.path)}
            />
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 inset-y-0 hidden items-center justify-center lg:flex">
          <div className="pointer-events-auto w-full">
            <PageHeaderTitle />
          </div>
        </div>

        <div className="ml-auto flex items-center justify-end gap-3 lg:z-10">
          <div className="hidden h-[var(--header-control-height)] lg:flex">
            <HeaderActions
              homePath={homePath}
              navigate={navigate}
              onRefresh={handleRefresh}
              loading={loading}
              isChangingCompany={isChangingCompany}
              showRefreshButton={showRefreshButton}
            />
          </div>

          <div className="flex lg:hidden">
            {showRefreshButton && atualizarDados && (
              <IconButton
                title={loading || isChangingCompany ? 'Atualizando dados...' : 'Atualizar dados'}
                onClick={handleRefresh}
                disabled={loading || isChangingCompany}
              >
                <RefreshCw className={`h-4 w-4 ${(loading || isChangingCompany) ? 'animate-spin' : ''}`} />
              </IconButton>
            )}
          </div>

          <div className="hidden h-[var(--header-control-height)] lg:block">
            <UserMenu
              onProfile={() => navigate('/perfil')}
              onLogout={() => setIsLogoutModalOpen(true)}
              user={user}
              canAccessSettings={canAccessSettings}
              canAccessRequests={canAccessRequests}
              pendingCount={pendingCount}
              onNavigateToSettings={() => navigate('/configuracoes')}
              onNavigateToRequests={() => navigate('/gestao-solicitacoes')}
              avatarColor={companyPalette.primary}
              userEmail={user?.corporate_email || user?.email}
              accentColor={companyPalette.primary}
              currentTheme={currentTheme}
              onUpdateTheme={updateThemePreference}
            />
          </div>
        </div>
      </HeaderShell>

      <MobileNavSheet
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        user={user}
        selectedCompany={selectedCompany}
        companyPalette={companyPalette}
        statusMessage={statusMessage}
        homePath={homePath}
        directoryItems={navigationItems}
        currentDirectory={currentDirectory}
        isGlobalDirectoryRoute={isGlobalDirectoryRoute}
        onNavigate={handleNavigateAndCloseDrawer}
        onSelectDirectory={(directory) => handleNavigateAndCloseDrawer(directory.path)}
        filteredCompanies={filteredCompanies}
        companySearch={companySearch}
        onSearchChange={setCompanySearch}
        onSelectCompany={(company) => handleCompanyChange(company, { closeDrawer: true })}
        availableCompanies={user?.accessible_companies}
        showRefreshButton={showRefreshButton}
        onRefresh={handleRefresh}
        loading={loading}
        isChangingCompany={isChangingCompany}
        canAccessSettings={canAccessSettings}
        canAccessRequests={canAccessRequests}
        pendingCount={pendingCount}
        currentTheme={currentTheme}
        onUpdateTheme={updateThemePreference}
        onLogout={() => {
          setOpenDrawer(false);
          setIsLogoutModalOpen(true);
        }}
      />

      <LogoutModal
        open={isLogoutModalOpen}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        loading={isLoggingOut}
      />
    </>
  );
};

export default GlobalHeader;
